export interface ChangelogEntry {
  date: string; // YYYY-MM-DD
  items: string[];
}

/**
 * User-facing changelog — plain English, no technical details.
 * Add a new entry each time something ships. Most recent first.
 */
export const changelog: ChangelogEntry[] = [
  {
    date: '2026-11-02',
    items: [
      'Every dark sky location page now has a written guide to the place: who runs it, how dark it gets, when the core is best and how to plan a visit.',
      'The dark sky locations list now links all 397 locations, and there are new pages for each country and US state, such as Dark Sky Locations in Utah. Location guides, blog posts and lessons link to the location pages they mention.',
      'Corrected details for about 80 locations that disagreed with their official listings: wrong states or countries (Salinas Pueblo Missions was listed in Wyoming), map points in the wrong place, names, designation years and areas. Light pollution levels and best months are recalculated at the corrected points. Old links to renamed locations still work.',
      'Fixed a rare case where a complimentary Premium account could lose Premium after a brief server error.',
      'The contact form now tells you right away if your email address doesn\'t look right or a field is too long, before you send.',
      'The donation thank-you page no longer redraws itself after it loads.',
      'Behind-the-scenes security fixes for the contact form and shared calendar emails.',
    ],
  },
  {
    date: '2026-11-01',
    items: [
      'Shared trip and calendar links open straight to the trip or calendar. They no longer show the homepage for a moment first.',
      'The dark sky locations list no longer redraws itself after it loads.',
      'Photo uploads now tell you why a photo was refused before it uploads: HEIC and SVG files aren\'t supported, and the limit is 10 MB. If you pick several photos and one is refused, the rest still upload.',
      'Behind-the-scenes security fix for the donation thank-you page.',
    ],
  },
  {
    date: '2026-10-30',
    items: [
      'Links to a shared trip or a shared calendar now show that trip\'s or calendar\'s own name and description when you post them, and search engines can find them.',
      'The trip and calendar PDFs now give each night the same rating as the planner. Before, about one night in 20 scored a point lower in the PDF.',
      'Fixed a Safari bug where a dark sky location page could stay blank when you opened it from an old link.',
      'Workshop leaders who come from the LEADER50 offer now see the $100/year price on the sign-up page, and the discount is applied at checkout.',
      'The Workshops and Instructors pages now show up in search results as their own pages.',
      'Old links to /learn and /moon-planner now go straight to the Intro to Astrophotography course and the features page.',
      'Photo uploads (workshop photos and profile pictures) take JPEG, PNG, WebP, GIF or AVIF files up to 10 MB.',
      'The planner\'s Premium section now lists what Premium adds: the Moon Planner, the Trip Planner, and PDF, Excel, CSV and calendar exports. Saving locations is free with an account. The planner\'s annual calendar link now opens the 2027 calendar.',
      'Behind-the-scenes security fixes for newsletter sign-ups, account and workshop emails, the contact form, checkout, photo uploads and free-plan usage counts.',
    ],
  },
  {
    date: '2026-10-20',
    items: [
      'New for Premium: dark core time. For each night you now see how long the galactic core is up while the sky is fully dark and the moon is below the horizon, for example "3h 20m". It\'s in the planner\'s map panel, the Excel and CSV exports, the calendar files and the PDF calendars.',
      'Moonrise and moonset times are more accurate. They now match the US Naval Observatory\'s published times to the minute. Most times move by a few minutes, and some by up to about 20.',
      'Sunrise, sunset and twilight times are more accurate too, which also sharpens when full darkness starts and ends. Most move by a minute or two. Far-north summer nights, where darkness barely arrives, can move by up to about 10 minutes, and a handful of nights at the edge of the season change between visible and not visible.',
      'Times now round to the nearest minute, the way Stellarium shows them. Before, the seconds were cut off, so some times read a minute early.',
      'Dark sky location pages load with their details straight away, and search engines can now read them. The "Nearby Dark Sky Locations" links on each page work again.',
      'On a shared calendar, the "Export to My Calendar" file includes which way to point for Premium members only, the same as the rest of the site.',
      'The PDF from a shared trip link shows each night\'s dark core time and which way to point for Premium members only. Everyone with the link still gets the PDF.',
    ],
  },
  {
    date: '2026-10-04',
    items: [
      'New for Premium: which way to point and how high. Pick a night on the planner to see where the galactic core is when the dark window starts, when it peaks (with the time) and when it ends, as a compass direction and degrees above the horizon. The same details are in the Excel and CSV exports, the calendar files and the PDF calendars.',
      'Galactic core positions and times are more accurate. They now match professional astronomy software to within a few hundredths of a degree and about a minute, and use the height you actually see (including the atmosphere\'s bending of light near the horizon). On some nights the core start or end time moves by a few minutes, and a handful of nights at the edge of the season change between visible and not visible.',
    ],
  },
  {
    date: '2026-10-02',
    items: [
      'Times in the PDF calendars (annual and monthly), the moon calendar PDF and the trip PDF now show in your location\'s local time, matching the Excel export and the planner. They were printing in UTC, which put them several hours off.',
      'Fixed blog posts, Learn lessons and location guides failing to load on iPhones running iOS older than 16.4.',
      'The trip PDF now shows each night\'s own sunrise, sunset and Milky Way times (they were a day early for stops in the Americas) and no longer counts your departure day as a night.',
      'Behind-the-scenes security fix for the Premium PDF downloads.',
      'Moonrise and moonset now follow the location\'s own day everywhere (planner, Excel, calendar files and PDFs), so they no longer change depending on your device\'s timezone.',
    ],
  },
  {
    date: '2026-09-28',
    items: [
      'New for Premium: a weather verdict for tonight and tomorrow night at each saved location. It reads cloud (low, mid and high), smoke and haze, and wind for the dark hours, and tells you whether the sky is worth the drive. When the forecast is too mixed to call, it shows you the numbers and leaves the call to you.',
      'Complimentary Premium accounts now unlock right away.',
      'Fixed a crash that could stop the site from loading for people browsing in private/incognito mode, or with site data blocked.',
      'Fixed a rare map crash when navigating away from a dark sky location page on mobile.',
      'The "Get Premium" button and the Excel/CSV exports now show a spinner while they work and tell you if something goes wrong, instead of silently doing nothing.',
      'Fixed a broken image in the Milky Way arch blog post.',
    ],
  },
  {
    date: '2026-07-01',
    items: [
      'Fixed "Get Premium" checkout — clicking it now takes you straight to the secure Stripe checkout page instead of appearing to do nothing.',
      'The planner\'s "Copy Link" button now copies the shareable link straight to your clipboard.',
      'Password-reset emails now link back to the correct page.',
      'Adding a favorite location is now instant — its light-pollution (Bortle) rating fills in a moment later instead of making you wait for it.',
      'The light-pollution map overlay loads reliably again.',
      'Accessibility improvements across the site: clearer screen-reader labels, better keyboard navigation, clearer form errors, and respect for your "reduce motion" setting.',
      'A range of behind-the-scenes security and reliability hardening.',
    ],
  },
  {
    date: '2026-06-05',
    items: [
      'Newsletter emails now show blog image captions with proper styling (centered, italic) to match the website, instead of plain text under the image.',
      'Fixed a rare error screen that could appear if the site was updated while you had it open — affected pages now reload automatically to the latest version instead of showing an error.',
    ],
  },
  {
    date: '2026-04-22',
    items: [
      'Fixed a rare crash on the planner for Safari users and anyone using a browser translator (Chrome Translate, Google Translate extension, etc.).',
      'Bortle class tags on your saved locations now fill in reliably and stick — the first time your dashboard can read the Bortle class for a location, it\'s saved so future visits show the tag instantly.',
    ],
  },
  {
    date: '2026-04-11',
    items: [
      'Location search now finds parks and places even with slightly different spellings — e.g. "Great Smoky Mountain" now finds "Great Smoky Mountains National Park".',
      'New blog post: Lyrids 2026 — everything you need to know to photograph the April meteor shower.',
    ],
  },
  {
    date: '2026-04-06',
    items: [
      'Changelog page added — you can now see a history of updates at milkywayplanner.com/changelog.',
    ],
  },
  {
    date: '2026-04-05',
    items: [
      'Click anywhere on the map to instantly see the light pollution level (Bortle class) for that spot.',
      'Dark Sky Parks overlay — see IDA-certified dark sky areas displayed directly on the map.',
      'Light pollution level is now saved with each of your favorite locations so it shows up without a lookup.',
      'Bortle class badge now appears in the planner header as soon as you enter a location.',
      'Fixed several stability issues on iOS and Safari.',
    ],
  },
  {
    date: '2026-04-03',
    items: [
      'New blog post: Scout Now, Shoot Later — how to plan locations months in advance.',
    ],
  },
  {
    date: '2026-03-31',
    items: [
      'Light pollution overlay — toggle a color-coded map of light pollution levels on top of any map on the site.',
      'New page: /light-pollution with an interactive map and guide to understanding dark sky conditions.',
      'New blog post: Understanding dark sky maps, light pollution, and the galactic core arc.',
      'Improved mobile layout for the annual Milky Way calendar heatmap.',
    ],
  },
  {
    date: '2026-03-26',
    items: [
      'Learn section launched — 8 free lessons covering astrophotography technique, from camera settings to processing.',
    ],
  },
  {
    date: '2026-03-25',
    items: [
      'Trip planner: weather forecasts added to each stop so you can see conditions at a glance.',
      'Trip planner: drive times and route lines between stops shown on the map.',
      'Trip planner: share your trip publicly with a link so others can view it.',
      'Trip planner: climate-aware packing checklist generated based on your destinations.',
      'Trip planner: shooting quality score for each night, combining moon, Bortle class, and galactic core timing.',
      'Moonrise and moonset times added to the shared trip view.',
      'Locations page loads faster with better search and pagination.',
    ],
  },
  {
    date: '2026-03-24',
    items: [
      'Trip planner: Bortle class badge on each stop card.',
      'Trip planner: shooting quality score included in Excel and CSV exports.',
      'Trip planner: best-night highlights and trip summary now show shooting quality.',
    ],
  },
  {
    date: '2025-06-07',
    items: [
      'Forgot password flow added.',
      'Welcome email sent when you create an account.',
      'Fixed redirect after a successful upgrade to Premium.',
    ],
  },
  {
    date: '2025-06-06',
    items: [
      'Premium subscriptions launched — unlock unlimited planning for your location.',
      'Export your calendar to a spreadsheet (XLSX) with moon phase and visibility ratings.',
      'Moon phase percentage and visibility rating added to the calendar.',
      'Typeahead location search — start typing and matching cities appear instantly.',
      'About page added.',
    ],
  },
];
